import React, { useState, useCallback, useEffect } from 'react';
import { Select } from 'antd';
import apiClient from '../../../services/apiClient';
import { FJ510 } from '../../../services/j214Api';

interface Tab1Props {
  formData: Partial<FJ510>;
  isEditing: boolean;
  getField: (key: keyof FJ510) => FJ510[keyof FJ510];
  setField: (key: keyof FJ510, value: FJ510[keyof FJ510]) => void;
  detailCount?: number;
  onOpenDetails?: () => void;
}

/** Generic Lookup Select -- fetches from /api/lookup/:type */
const LookupSelect: React.FC<{
  lookupType: string;
  value: string;
  disabled: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  onChange: (v: string) => void;
}> = ({ lookupType, value, disabled, placeholder, style, onChange }) => {
  const [options, setOptions] = useState<{ code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: '1', size: '50' };
      if (keyword) params.keyword = keyword;
      const res = await apiClient.get(`/api/lookup/${lookupType}`, { params });
      setOptions(res.data.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [lookupType]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={loading}
      value={value || undefined}
      placeholder={placeholder || '...'}
      style={style || { width: '100%' }}
      filterOption={false}
      onSearch={v => fetchOptions(v)}
      onChange={v => onChange(v || '')}
      options={options.map(o => ({ value: o.code, label: `${o.code} - ${o.name}` }))}
    />
  );
};

const dateToInput = (v: FJ510[keyof FJ510]): string => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.substring(0, 10) : s;
};

const Tab1BasicInfo: React.FC<Tab1Props> = ({ formData, isEditing, getField, setField, detailCount = 0, onOpenDetails }) => {
  const isDisabled = !isEditing;

  return (
    <div className="j214-tab1">
      {/* Section 1: Basic Info */}
      <div className="form-section">
        <span className="section-tag">採購基本資訊</span>
        <div className="form-grid">
          {/* fj510_01 採購單號 (readOnly) */}
          <label data-contract-field="fj510_01">採購單號</label>
          <input
            type="text"
            readOnly
            value={getField('fj510_01') as string}
            style={{ maxWidth: 140 }}
          />

          {/* fj510_11 採購日期 */}
          <label className="required" data-contract-field="fj510_11">採購日期</label>
          <input
            type="date"
            disabled={isDisabled}
            value={dateToInput(getField('fj510_11'))}
            onChange={e => setField('fj510_11', e.target.value || null)}
            style={{ maxWidth: 160 }}
          />

          {/* fj510_12 公司代號 (Lookup) */}
          <label className="required" data-contract-field="fj510_12">公司代號</label>
          <LookupSelect
            lookupType="customer"
            value={getField('fj510_12') as string}
            disabled={isDisabled}
            placeholder="選擇公司"
            style={{ maxWidth: 220 }}
            onChange={v => setField('fj510_12', v)}
          />

          {/* fj510_21 交貨日期 */}
          <label className="required" data-contract-field="fj510_21">交貨日期</label>
          <input
            type="date"
            disabled={isDisabled}
            value={dateToInput(getField('fj510_21'))}
            onChange={e => setField('fj510_21', e.target.value || null)}
            style={{ maxWidth: 160 }}
          />

          {/* fj510_26 送貨地點 */}
          <label data-contract-field="fj510_26">送貨地點</label>
          <input
            type="text"
            maxLength={50}
            disabled={isDisabled}
            value={getField('fj510_26') as string}
            onChange={e => setField('fj510_26', e.target.value)}
            style={{ maxWidth: 300 }}
          />

          {/* fj510_30 幣別 (Lookup) */}
          <label className="required" data-contract-field="fj510_30">幣別</label>
          <LookupSelect
            lookupType="currency"
            value={getField('fj510_30') as string}
            disabled={isDisabled}
            placeholder="選擇幣別"
            style={{ maxWidth: 140 }}
            onChange={v => setField('fj510_30', v)}
          />

          {/* fj510_27 單位 */}
          <label className="required" data-contract-field="fj510_27">單位</label>
          <input
            type="text"
            maxLength={3}
            disabled={isDisabled}
            value={getField('fj510_27') as string}
            onChange={e => setField('fj510_27', e.target.value)}
            style={{ maxWidth: 100 }}
          />

          {/* fj510_22 包裝方式 (Lookup) */}
          <label className="required" data-contract-field="fj510_22">包裝方式</label>
          <LookupSelect
            lookupType="packaging"
            value={getField('fj510_22') as string}
            disabled={isDisabled}
            placeholder="選擇包裝方式"
            style={{ maxWidth: 160 }}
            onChange={v => setField('fj510_22', v)}
          />
        </div>
      </div>

      {/* Section 2: Product Info */}
      <div className="form-section">
        <span className="section-tag">產品資訊</span>
        <div className="form-grid">
          {/* fj510_13 + fj510_131 產品代號 */}
          <label data-contract-field="fj510_13">產品代號</label>
          <div style={{ display: 'flex', gap: 4, maxWidth: 240 }}>
            <input
              type="text"
              maxLength={6}
              disabled={isDisabled}
              value={getField('fj510_13') as string}
              onChange={e => setField('fj510_13', e.target.value)}
              placeholder="前6碼"
              style={{ width: '50%' }}
            />
            <input
              type="text"
              maxLength={6}
              disabled={isDisabled}
              value={getField('fj510_131') as string}
              onChange={e => setField('fj510_131', e.target.value)}
              placeholder="後6碼"
              style={{ width: '50%' }}
            />
          </div>

          {/* fj510_14 產品名稱 (editbox -> TextArea) */}
          <label data-contract-field="fj510_14">產品名稱</label>
          <textarea
            disabled={isDisabled}
            value={getField('fj510_14') as string}
            onChange={e => setField('fj510_14', e.target.value)}
            rows={2}
            style={{ maxWidth: 400 }}
          />

          {/* fj510_15 產品規格 (editbox -> TextArea) */}
          <label data-contract-field="fj510_15">產品規格</label>
          <textarea
            disabled={isDisabled}
            value={getField('fj510_15') as string}
            onChange={e => setField('fj510_15', e.target.value)}
            rows={2}
            style={{ maxWidth: 400 }}
          />

          {/* fj510_16 加工別 (editbox -> TextArea) */}
          <label data-contract-field="fj510_16">加工別</label>
          <textarea
            disabled={isDisabled}
            value={getField('fj510_16') as string}
            onChange={e => setField('fj510_16', e.target.value)}
            rows={2}
            style={{ maxWidth: 400 }}
          />
        </div>
      </div>

      {/* Section 3: Specifications */}
      <div className="form-section">
        <span className="section-tag">幅寬/重量</span>
        <div className="form-grid">
          {/* fj510_17 針孔-內/外 + fj510_18 (paired numeric) */}
          <label data-contract-field="fj510_17">針孔-內/外</label>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', maxWidth: 260 }}>
            <input
              type="number"
              step="0.1"
              disabled={isDisabled}
              value={getField('fj510_17') as number ?? 0}
              onChange={e => setField('fj510_17', parseFloat(e.target.value) || 0)}
              style={{ width: 80 }}
            />
            <span>/</span>
            <input
              type="number"
              step="0.1"
              disabled={isDisabled}
              value={getField('fj510_18') as number ?? 0}
              onChange={e => setField('fj510_18', parseFloat(e.target.value) || 0)}
              style={{ width: 80 }}
            />
            <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>Inch</span>
          </div>

          {/* fj510_19 碼重 */}
          <label data-contract-field="fj510_19">碼重</label>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', maxWidth: 180 }}>
            <input
              type="number"
              step="0.1"
              disabled={isDisabled}
              value={getField('fj510_19') as number ?? 0}
              onChange={e => setField('fj510_19', parseFloat(e.target.value) || 0)}
              style={{ width: 120 }}
            />
            <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>G/Y</span>
          </div>

          {/* fj510_20 米平方重 */}
          <label data-contract-field="fj510_20">米平方重</label>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', maxWidth: 180 }}>
            <input
              type="number"
              step="0.1"
              disabled={isDisabled}
              value={getField('fj510_20') as number ?? 0}
              onChange={e => setField('fj510_20', parseFloat(e.target.value) || 0)}
              style={{ width: 120 }}
            />
            <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>GM/SQM</span>
          </div>
        </div>
      </div>

      {/* Section 4: Remarks & Totals */}
      <div className="form-section">
        <span className="section-tag">備註與合計</span>
        <div className="form-grid">
          {/* fj510_24 備註 (Lookup) */}
          <label data-contract-field="fj510_24">備註</label>
          <LookupSelect
            lookupType="remark"
            value={getField('fj510_24') as string}
            disabled={isDisabled}
            placeholder="選擇備註代號"
            style={{ maxWidth: 160 }}
            onChange={v => setField('fj510_24', v)}
          />

          {/* fj510_25 備註內容 (editbox -> TextArea) */}
          <label data-contract-field="fj510_25">備註內容</label>
          <textarea
            disabled={isDisabled}
            value={getField('fj510_25') as string}
            onChange={e => setField('fj510_25', e.target.value)}
            rows={3}
            style={{ maxWidth: 400 }}
          />

          {/* fj510_29 未稅總金額 (readOnly) */}
          <label data-contract-field="fj510_29">未稅總金額</label>
          <input
            type="text"
            readOnly
            value={Number(getField('fj510_29') || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            style={{ maxWidth: 160, textAlign: 'right' }}
          />

          {/* fj510_23 數量 (readOnly) */}
          <label data-contract-field="fj510_23">數量</label>
          <input
            type="text"
            readOnly
            value={Number(getField('fj510_23') || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            style={{ maxWidth: 160, textAlign: 'right' }}
          />
        </div>
      </div>

      {/* FoxPro Command1: 顏色明細 button — opens detail tab */}
      {onOpenDetails && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={onOpenDetails}
            style={{
              padding: '6px 20px',
              fontSize: 14,
              color: '#1890ff',
              border: '1px solid #1890ff',
              borderRadius: 4,
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            顏色明細 ({detailCount})
          </button>
        </div>
      )}
    </div>
  );
};

export default Tab1BasicInfo;
